
import React, { useState, useEffect, useContext } from 'react';
import { Company, Tenant, UserRole } from '../../types';
import { AuthContext } from '../../contexts/AuthContext';
import { firestoreService } from '../../services/firestoreService';
import Spinner from '../common/Spinner';

interface AddCompanyModalProps {
    onClose: () => void;
    onSave: (companyData: Omit<Company, 'companyId' | 'createdAt'> | Company) => Promise<void>;
    companyToEdit?: Company | null;
}

const AddCompanyModal: React.FC<AddCompanyModalProps> = ({ onClose, onSave, companyToEdit }) => {
    const authContext = useContext(AuthContext);
    const isPlatformOwner = authContext?.user?.role === UserRole.PLATFORM_OWNER;

    const [name, setName] = useState('');
    const [selectedTenantId, setSelectedTenantId] = useState('');
    const [tenants, setTenants] = useState<Tenant[]>([]);
    const [primaryColor, setPrimaryColor] = useState('#FFFFFF');
    const [backgroundColor, setBackgroundColor] = useState('#3B82F6');
    const [logoUrl, setLogoUrl] = useState('');
    const [bannerText, setBannerText] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [loadingTenants, setLoadingTenants] = useState(false);

    const isEditing = !!companyToEdit;

    useEffect(() => {
        if (isEditing && companyToEdit) {
            const { branding } = companyToEdit.settings;
            setName(companyToEdit.name);
            setSelectedTenantId(companyToEdit.tenantId);
            setPrimaryColor(branding.primaryColor || '#FFFFFF');
            setBackgroundColor(branding.backgroundColor || '#3B82F6');
            setLogoUrl(branding.logoUrl || '');
            setBannerText(branding.bannerText || '');
        } else if (!isEditing && !isPlatformOwner && authContext?.user?.tenantId) {
            setSelectedTenantId(authContext.user.tenantId);
        }
    }, [companyToEdit, isEditing, isPlatformOwner, authContext?.user?.tenantId]);

    useEffect(() => {
        if (isPlatformOwner) {
            const fetchTenants = async () => {
                setLoadingTenants(true);
                try {
                    const data = await firestoreService.getTenants();
                    setTenants(data);
                    if (data.length > 0 && !selectedTenantId) {
                        setSelectedTenantId(data[0].tenantId);
                    }
                } catch (e) {
                    console.error("Error fetching tenants for modal", e);
                } finally {
                    setLoadingTenants(false);
                }
            };
            fetchTenants();
        }
    }, [isPlatformOwner]);


    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!authContext?.user) return;
        if (!selectedTenantId) {
            alert("Debes seleccionar una Agencia para este cliente.");
            return;
        }

        setIsSubmitting(true);
        const branding = { primaryColor, backgroundColor, logoUrl, bannerText };
        try {
            if (isEditing && companyToEdit) {
                const updatedCompanyData: Company = {
                    ...companyToEdit,
                    name,
                    tenantId: selectedTenantId,
                    settings: { ...companyToEdit.settings, branding }
                };
                await onSave(updatedCompanyData);
            } else {
                const newCompanyData: Omit<Company, 'companyId' | 'createdAt'> = {
                    name,
                    tenantId: selectedTenantId,
                    settings: {
                        branding,
                        defaultFilters: [],
                        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    },
                    createdBy: authContext.user.uid,
                };
                await onSave(newCompanyData);
            }
        } catch (error) {
            console.error("Error saving company", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/80 flex justify-center items-center z-50 overflow-y-auto p-4">
            <div className="bg-gray-900 border border-gray-800 rounded-2xl shadow-2xl p-8 w-full max-w-lg m-auto animate-fade-in-scale">
                <h2 className="text-2xl font-bold text-white mb-6">
                    {isEditing ? 'Editar Cliente' : 'Nuevo Cliente (Company)'}
                </h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                    
                    {isPlatformOwner && (
                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-2">Asignar a Agencia</label>
                            {loadingTenants ? (
                                <div className="py-2"><Spinner size="sm" /></div>
                            ) : (
                                <select 
                                    value={selectedTenantId} 
                                    onChange={(e) => setSelectedTenantId(e.target.value)}
                                    className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500 outline-none"
                                    required
                                >
                                    <option value="" disabled>Selecciona una agencia...</option>
                                    {tenants.map(t => (
                                        <option key={t.tenantId} value={t.tenantId}>{t.name}</option>
                                    ))}
                                </select>
                            )}
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-gray-400 mb-2">Nombre del Cliente / Empresa</label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500 outline-none"
                            placeholder="Ej: Restaurante Central"
                            required
                        />
                    </div>

                     <div>
                        <label className="block text-sm font-medium text-gray-400 mb-2">Texto del Banner (Opcional)</label>
                        <input
                            type="text"
                            value={bannerText}
                            onChange={(e) => setBannerText(e.target.value)}
                            className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500 outline-none"
                            placeholder="Ej: Portal de Calidad"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-400 mb-2">URL del Logo del Cliente</label>
                        <input
                            type="url"
                            value={logoUrl}
                            onChange={(e) => setLogoUrl(e.target.value)}
                            className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500 outline-none text-sm"
                            placeholder="https://example.com/logo.png"
                        />
                    </div>

                    <div className='grid grid-cols-2 gap-4'>
                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-2">Color Texto</label>
                            <input
                                type="color"
                                value={primaryColor}
                                onChange={(e) => setPrimaryColor(e.target.value)}
                                className="w-full h-10 px-1 py-1 bg-gray-800 border border-gray-700 rounded-lg cursor-pointer"
                            />
                        </div>
                         <div>
                            <label className="block text-sm font-medium text-gray-400 mb-2">Color Fondo</label>
                            <input
                                type="color"
                                value={backgroundColor}
                                onChange={(e) => setBackgroundColor(e.target.value)}
                                className="w-full h-10 px-1 py-1 bg-gray-800 border border-gray-700 rounded-lg cursor-pointer"
                            />
                        </div>
                    </div>

                    <div className="flex justify-end gap-4 pt-6">
                        <button type="button" onClick={onClose} className="px-5 py-2.5 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors">Cancelar</button>
                        <button type="submit" disabled={isSubmitting} className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-500 disabled:opacity-50 font-bold transition-all shadow-lg shadow-blue-900/20">
                            {isSubmitting ? <Spinner size="sm" /> : (isEditing ? 'Guardar Cambios' : 'Crear Cliente')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddCompanyModal;
